import { environment } from 'src/environments/environment';

function normalizeHost(hostname: string | null | undefined): string {
  const raw = (hostname ?? '').trim().toLowerCase();
  if (raw === '::1' || raw === '[::1]') return '::1';
  return raw.split(':')[0];
}

function matchesExactHost(hostname: string, candidates: string[] | undefined): boolean {
  return (candidates ?? []).some((candidate) => normalizeHost(candidate) === hostname);
}

function matchesHostPrefix(hostname: string, prefixes: string[] | undefined): boolean {
  return (prefixes ?? []).some((prefix) => {
    const normalizedPrefix = prefix.trim().toLowerCase();
    if (!normalizedPrefix) {
      return false;
    }

    if (normalizedPrefix.endsWith('-') || normalizedPrefix.endsWith('.')) {
      return hostname.startsWith(normalizedPrefix);
    }

    return hostname === normalizedPrefix
      || hostname.startsWith(`${normalizedPrefix}.`)
      || hostname.startsWith(`${normalizedPrefix}-`);
  });
}

function matchesHostSuffix(hostname: string, suffixes: string[] | undefined): boolean {
  return (suffixes ?? []).some((suffix) => {
    const normalizedSuffix = suffix.trim().toLowerCase();
    if (!normalizedSuffix) {
      return false;
    }

    const safeSuffix = normalizedSuffix.startsWith('.') ? normalizedSuffix : `.${normalizedSuffix}`;
    const bareSuffix = safeSuffix.slice(1);
    return hostname === bareSuffix || hostname.endsWith(safeSuffix);
  });
}

function getPreviewCandidateHosts(hostname: string): string[] {
  const normalizedHost = normalizeHost(hostname);
  if (!normalizedHost) {
    return [];
  }

  const candidates = [normalizedHost];
  const firstDotIndex = normalizedHost.indexOf('.');
  if (firstDotIndex > 0) {
    const leadingLabel = normalizedHost.slice(0, firstDotIndex);
    const remainingHost = normalizedHost.slice(firstDotIndex + 1);
    if (matchesHostPrefix(leadingLabel, environment.adminHostPrefixes)
      || matchesHostPrefix(leadingLabel, environment.compassHostPrefixes)
      || matchesHostPrefix(leadingLabel, environment.registerHostPrefixes)) {
      candidates.push(remainingHost);
    }
  }

  return candidates;
}

export function isAdminHost(hostname: string | null | undefined): boolean {
  const normalizedHost = normalizeHost(hostname);
  return !!normalizedHost && matchesHostPrefix(normalizedHost, environment.adminHostPrefixes ?? ['admin']);
}

export function isFittaraCompassHost(hostname: string | null | undefined): boolean {
  const normalizedHost = normalizeHost(hostname);
  return !!normalizedHost && matchesHostPrefix(normalizedHost, environment.compassHostPrefixes ?? ['fittaracompass']);
}

export function isRegisterHost(hostname: string | null | undefined): boolean {
  const normalizedHost = normalizeHost(hostname);
  return !!normalizedHost && matchesHostPrefix(normalizedHost, environment.registerHostPrefixes ?? ['register']);
}

export function isLocalDevelopmentHost(hostname: string | null | undefined): boolean {
  const host = normalizeHost(hostname);
  return host === 'localhost' || host === '127.0.0.1' || host === '::1';
}

/**
 * Registration is isolated to its dedicated host. Local and explicitly
 * classified preview hosts are allowed so the flow can be tested before the
 * register subdomain is deployed; ordinary client storefronts remain blocked.
 */
export function isRegistrationContextHost(hostname: string | null | undefined): boolean {
  return isRegisterHost(hostname) || isLocalDevelopmentHost(hostname) || isPreviewHost(hostname);
}

export function isStorefrontSharedHost(hostname: string | null | undefined): boolean {
  const normalizedHost = normalizeHost(hostname);
  if (!normalizedHost) {
    return false;
  }

  return matchesExactHost(normalizedHost, environment.sharedHostnames)
    || matchesHostPrefix(normalizedHost, environment.sharedHostPrefixes)
    || matchesHostSuffix(normalizedHost, environment.sharedHostSuffixes);
}

export function isPreviewHost(hostname: string | null | undefined): boolean {
  const candidates = getPreviewCandidateHosts(hostname ?? '');
  return candidates.some((candidate) => matchesHostPrefix(candidate, environment.sharedHostPrefixes)
    || matchesHostSuffix(candidate, environment.sharedHostSuffixes));
}

export function shouldUsePreviewPaymentBridge(hostname: string | null | undefined): boolean {
  return environment.usePreviewPaymentBridge === true && isPreviewHost(hostname);
}

export function isSharedHost(hostname: string | null | undefined): boolean {
  return isAdminHost(hostname) || isFittaraCompassHost(hostname) || isStorefrontSharedHost(hostname);
}

/** Local owner previews need the shop-owner form, not the platform-admin app or a storefront popup. */
export function isLocalOwnerSignIn(hostname: string, returnUrl: string | null | undefined): boolean {
  const path = (returnUrl ?? '').split(/[?#]/)[0];
  return !environment.production && environment.runtimeEnvironmentLabel === 'LOCAL'
    && isLocalDevelopmentHost(hostname)
    && (path === '/admin-dashboard' || path.startsWith('/admin-dashboard/'));
}